import React, { useState, useEffect, useRef, useMemo } from 'react';
import { auth } from '../../services/api';

const GlobalStyles = () => (
  <style>{`
    .property-modal-input[type="number"]::-webkit-outer-spin-button,
    .property-modal-input[type="number"]::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    .property-modal-input[type="number"] {
      -moz-appearance: textfield;
    }
  `}</style>
);

interface DropdownOption {
  value: string;
  display_name: string;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const dropdownStyles = {
  container: {
    position: 'relative' as const,
    width: '100%',
  },
  trigger: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    fontSize: '0.8125rem',
    color: '#1e293b',
    backgroundColor: '#fff',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  triggerDisabled: {
    backgroundColor: '#f8fafc',
    color: '#64748b',
    cursor: 'not-allowed',
  },
  triggerOpen: {
    borderColor: '#3b82f6',
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
  },
  placeholder: {
    color: '#94a3b8',
  },
  arrow: {
    width: '1rem',
    height: '1rem',
    color: '#64748b',
    transition: 'transform 0.2s',
  },
  arrowOpen: {
    transform: 'rotate(180deg)',
  },
  menu: {
    position: 'absolute' as const,
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '0.25rem',
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    zIndex: 50,
    maxHeight: '12rem',
    overflowY: 'auto' as const,
  },
  option: {
    padding: '0.5rem 0.75rem',
    fontSize: '0.8125rem',
    color: '#1e293b',
    cursor: 'pointer',
    transition: 'background-color 0.1s',
  },
  optionHover: {
    backgroundColor: '#f1f5f9',
  },
  optionSelected: {
    backgroundColor: '#eff6ff',
    color: '#3b82f6',
  },
};

const CustomDropdown: React.FC<CustomDropdownProps> = ({ options, value, onChange, placeholder = 'Select...', disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} style={dropdownStyles.container}>
      <div
        ref={triggerRef}
        style={{
          ...dropdownStyles.trigger,
          ...(disabled ? dropdownStyles.triggerDisabled : {}),
          ...(isOpen ? dropdownStyles.triggerOpen : {}),
        }}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span style={!selectedOption ? dropdownStyles.placeholder : undefined}>
          {selectedOption ? selectedOption.display_name : placeholder}
        </span>
        <svg
          style={{ ...dropdownStyles.arrow, ...(isOpen ? dropdownStyles.arrowOpen : {}) }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {isOpen && (
        <div style={{
          position: 'fixed',
          top: menuPosition.top,
          left: menuPosition.left,
          width: menuPosition.width,
          backgroundColor: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '0.375rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          zIndex: 9999,
          maxHeight: '12rem',
          overflowY: 'auto' as const,
        }}>
          {options.map((option, index) => (
            <div
              key={option.value}
              style={{
                ...dropdownStyles.option,
                ...(hoveredIndex === index ? dropdownStyles.optionHover : {}),
                ...(value === option.value ? dropdownStyles.optionSelected : {}),
              }}
              onClick={() => handleSelect(option.value)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {option.display_name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface Address {
  address_type: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
}

interface Property {
  id: number;
  client_id: number;
  property_name: string;
  service_point_name?: string;
  jump_service_point_id?: string;
  jump_service_point_seq?: number;
  jump_property_seq_no?: number;
  jump_property_id?: string;
  jump_property_group?: string;
  len_legal_entity_name?: string;
  energy_star_property_type?: string;
  occupancy_type?: string;
  occupancy_percent?: number;
  year_built?: number;
  tax_exempt_percent?: number;
  total_gross_floor_area?: number;
  number_of_buildings?: number;
  has_irrigated_area?: boolean;
  longitude?: number;
  latitude?: number;
  active: boolean;
  active_date?: string;
  addresses?: Address[];
}

interface PropertyModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyId?: number;
  mode: 'add' | 'edit';
  clientId: number;
  onSuccess?: () => void;
}

interface FormData extends Partial<Property> {
  primary_address_line1?: string;
  primary_address_line2?: string;
  primary_city?: string;
  primary_state?: string;
  primary_zip_code?: string;
  service_address_line1?: string;
  service_address_line2?: string;
  service_city?: string;
  service_state?: string;
  service_zip_code?: string;
  billing_address_line1?: string;
  billing_address_line2?: string;
  billing_city?: string;
  billing_state?: string;
  billing_zip_code?: string;
}

const styles = {
  backdrop: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '0.75rem',
    width: '100%',
    maxWidth: '75rem',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: '0 1.5rem 3rem rgba(0, 0, 0, 0.2)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#1e293b',
    margin: 0,
  },
  statusToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.75rem',
    color: '#64748b',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.5rem',
    color: '#64748b',
    borderRadius: '0.375rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    padding: '1.5rem',
    overflowY: 'auto' as const,
    flex: 1,
  },
  cardsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1rem',
    marginBottom: '1rem',
  },
  card: {
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    backgroundColor: '#fff',
    overflow: 'hidden',
  },
  cardHeader: {
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    padding: '0.625rem 1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  cardHeaderIcon: {
    width: '1rem',
    height: '1rem',
    color: '#3b82f6',
  },
  cardTitle: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: '#3b82f6',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    margin: 0,
  },
  cardBody: {
    padding: '1rem',
  },
  fieldGrid: {
    display: 'grid',
    gap: '0.875rem',
  },
  fieldGridTwoCol: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  label: {
    fontSize: '0.6875rem',
    fontWeight: 500,
    color: '#64748b',
  },
  required: {
    color: '#ef4444',
  },
  input: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    fontSize: '0.8125rem',
    color: '#1e293b',
    backgroundColor: '#fff',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  inputDisabled: {
    backgroundColor: '#f8fafc',
    color: '#64748b',
  },
  inputError: {
    borderColor: '#ef4444',
  },
  select: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    fontSize: '0.8125rem',
    color: '#1e293b',
    backgroundColor: '#fff',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  errorText: {
    fontSize: '0.625rem',
    color: '#ef4444',
  },
  addressRow: {
    display: 'grid',
    gridTemplateColumns: '5.5rem 1fr 4rem',
    gap: '0.5rem',
  },
  toggle: {
    width: '2.5rem',
    height: '1.25rem',
    borderRadius: '0.625rem',
    cursor: 'pointer',
    position: 'relative' as const,
    transition: 'background-color 0.2s',
  },
  toggleOn: {
    backgroundColor: '#3b82f6',
  },
  toggleOff: {
    backgroundColor: '#cbd5e1',
  },
  toggleSlider: {
    width: '1rem',
    height: '1rem',
    backgroundColor: '#fff',
    borderRadius: '50%',
    position: 'absolute' as const,
    top: '0.125rem',
    transition: 'left 0.2s',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
  },
  toggleSliderOn: {
    left: '1.375rem',
  },
  toggleSliderOff: {
    left: '0.125rem',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
  },
  btnCancel: {
    padding: '0.5rem 1rem',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    backgroundColor: '#fff',
    color: '#64748b',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  btnSave: {
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '0.375rem',
    backgroundColor: '#3b82f6',
    color: '#fff',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  btnSaveDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
  },
  successMessage: {
    color: '#16a34a',
    margin: '0 0 1rem 0',
    textAlign: 'center' as const,
    padding: '0.75rem',
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '0.375rem',
  },
  errorMessage: {
    color: '#dc2626',
    margin: '0 0 1rem 0',
    textAlign: 'center' as const,
    padding: '0.75rem',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '0.375rem',
  },
};

const PropertyModal: React.FC<PropertyModalProps> = ({
  isOpen,
  onClose,
  propertyId,
  mode,
  clientId,
  onSuccess
}) => {
  const [formData, setFormData] = useState<FormData>({
    client_id: clientId,
    service_point_name: '',
    jump_service_point_id: '',
    jump_service_point_seq: undefined,
    len_legal_entity_name: '',
    energy_star_property_type: '',
    year_built: undefined,
    number_of_buildings: undefined,
    total_gross_floor_area: undefined,
    occupancy_type: '',
    occupancy_percent: undefined,
    tax_exempt_percent: undefined,
    has_irrigated_area: false,
    active: true,
    primary_address_line1: '',
    primary_address_line2: '',
    primary_city: '',
    primary_state: '',
    primary_zip_code: '',
    service_address_line1: '',
    service_address_line2: '',
    service_city: '',
    service_state: '',
    service_zip_code: '',
    billing_address_line1: '',
    billing_address_line2: '',
    billing_city: '',
    billing_state: '',
    billing_zip_code: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [propertyTypes, setPropertyTypes] = useState<Array<{value: string, display_name: string}>>([]);
  const [occupancyTypes, setOccupancyTypes] = useState<Array<{value: string, display_name: string}>>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [originalFormData, setOriginalFormData] = useState<FormData | null>(null);

  const isDirty = useMemo(() => {
    if (!originalFormData) return mode === 'add';
    const normalize = (val: unknown) => (val === null || val === undefined || val === '') ? '' : String(val);
    const keys = Object.keys(formData) as (keyof FormData)[];
    return keys.some(key => normalize(formData[key]) !== normalize(originalFormData?.[key]));
  }, [formData, mode, originalFormData]);


  useEffect(() => {
    const fetchLookupValues = async () => {
      try {
        const [propertyTypesResponse, occupancyTypesResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/admin/lookup-values?category=property_type`, {
            headers: { ...auth.getAuthHeaders() }
          }),
          fetch(`${process.env.REACT_APP_API_URL}/api/admin/lookup-values?category=occupancy_type`, {
            headers: { ...auth.getAuthHeaders() }
          })
        ]);

        if (propertyTypesResponse.ok) {
          setPropertyTypes(await propertyTypesResponse.json());
        }
        if (occupancyTypesResponse.ok) {
          setOccupancyTypes(await occupancyTypesResponse.json());
        }
      } catch (error) {
        console.error('Error fetching lookup values:', error);
      }
    };

    if (isOpen) {
      fetchLookupValues();
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchProperty = async () => {
      setErrors({});
      setValidationErrors({});
      if (propertyId && propertyId > 0 && mode === 'edit') {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/properties/${propertyId}`, {
            headers: { ...auth.getAuthHeaders() }
          });
          if (response.ok) {
            const property = await response.json();
            const formDataWithAddresses: FormData = {
              ...property,
              primary_address_line1: '',
              primary_address_line2: '',
              primary_city: '',
              primary_state: '',
              primary_zip_code: '',
              service_address_line1: '',
              service_address_line2: '',
              service_city: '',
              service_state: '',
              service_zip_code: '',
              billing_address_line1: '',
              billing_address_line2: '',
              billing_city: '',
              billing_state: '',
              billing_zip_code: ''
            };

            if (property.addresses) {
              property.addresses.forEach((address: Address) => {
                if (address.address_type === 'primary') {
                  formDataWithAddresses.primary_address_line1 = address.address_line1;
                  formDataWithAddresses.primary_address_line2 = address.address_line2 || '';
                  formDataWithAddresses.primary_city = address.city;
                  formDataWithAddresses.primary_state = address.state;
                  formDataWithAddresses.primary_zip_code = address.zip_code;
                } else if (address.address_type === 'service') {
                  formDataWithAddresses.service_address_line1 = address.address_line1;
                  formDataWithAddresses.service_address_line2 = address.address_line2 || '';
                  formDataWithAddresses.service_city = address.city;
                  formDataWithAddresses.service_state = address.state;
                  formDataWithAddresses.service_zip_code = address.zip_code;
                } else if (address.address_type === 'billing') {
                  formDataWithAddresses.billing_address_line1 = address.address_line1;
                  formDataWithAddresses.billing_address_line2 = address.address_line2 || '';
                  formDataWithAddresses.billing_city = address.city;
                  formDataWithAddresses.billing_state = address.state;
                  formDataWithAddresses.billing_zip_code = address.zip_code;
                }
              });
            }

            setFormData(formDataWithAddresses);
            setOriginalFormData({ ...formDataWithAddresses });
          }
        } catch (error) {
          console.error('Error fetching property:', error);
        }
      } else if (mode === 'add') {
        setFormData({
          client_id: clientId,
          service_point_name: '',
          jump_service_point_id: '',
          jump_service_point_seq: undefined,
          len_legal_entity_name: '',
          energy_star_property_type: '',
          year_built: undefined,
          number_of_buildings: undefined,
          total_gross_floor_area: undefined,
          occupancy_type: '',
          occupancy_percent: undefined,
          tax_exempt_percent: undefined,
          has_irrigated_area: false,
          active: true,
          primary_address_line1: '',
          primary_address_line2: '',
          primary_city: '',
          primary_state: '',
          primary_zip_code: '',
          service_address_line1: '',
          service_address_line2: '',
          service_city: '',
          service_state: '',
          service_zip_code: '',
          billing_address_line1: '',
          billing_address_line2: '',
          billing_city: '',
          billing_state: '',
          billing_zip_code: ''
        });
        setOriginalFormData(null);
      }
    };

    if (isOpen) {
      fetchProperty();
    }
  }, [propertyId, mode, clientId, isOpen]);

  const integerFields = ['jump_service_point_seq', 'year_built', 'number_of_buildings', 'total_gross_floor_area'];
  const decimalFields = ['occupancy_percent', 'tax_exempt_percent'];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (integerFields.includes(name)) {
      const cleanValue = value.replace(/[^\d]/g, '');
      const numValue = cleanValue === '' ? undefined : Number(cleanValue);
      setFormData(prev => ({ ...prev, [name]: numValue }));
    } else if (decimalFields.includes(name)) {
      const cleanValue = value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
      setFormData(prev => ({ ...prev, [name]: cleanValue === '' ? undefined : cleanValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleZipCodeChange = async (e: React.ChangeEvent<HTMLInputElement>, addressType: 'primary' | 'service' | 'billing') => {
    const zipCode = e.target.value;
    setFormData(prev => ({ ...prev, [`${addressType}_zip_code`]: zipCode }));

    if (zipCode.length === 5 && /^\d{5}$/.test(zipCode)) {
      try {
        const response = await fetch(`https://api.zippopotam.us/us/${zipCode}`);
        if (response.ok) {
          const data = await response.json();
          setFormData(prev => ({
            ...prev,
            [`${addressType}_city`]: data.places[0]['place name'],
            [`${addressType}_state`]: data.places[0]['state abbreviation']
          }));
        }
      } catch (error) {
        console.log('Could not auto-populate city/state for zip code:', zipCode);
      }
    }
  };

  const validateServicePointId = async (value: string) => {
    if (!value || !value.trim()) {
      setValidationErrors(prev => ({ ...prev, jump_service_point_id: '' }));
      return;
    }

    try {
      const excludeParam = mode === 'edit' && propertyId ? `?excludePropertyId=${propertyId}` : '';
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/properties/validate-service-point-id/${clientId}/${value}${excludeParam}`,
        { headers: { ...auth.getAuthHeaders() } }
      );
      if (response.ok) {
        const result = await response.json();
        if (!result.isValid) {
          setValidationErrors(prev => ({
            ...prev,
            jump_service_point_id: `Already assigned to "${result.existingProperty.name}" - Reverting back`
          }));
          setTimeout(() => {
            setFormData(prev => ({
              ...prev,
              jump_service_point_id: originalFormData?.jump_service_point_id || ''
            }));
            setValidationErrors(prev => ({ ...prev, jump_service_point_id: '' }));
          }, 2000);
        } else {
          setValidationErrors(prev => ({ ...prev, jump_service_point_id: '' }));
        }
      }
    } catch (error) {
      console.error('Error validating service point ID:', error);
    }
  };

  const validateServicePointSeq = async (inputValue: string) => {
    const value = inputValue.trim();
    if (!value) {
      setValidationErrors(prev => ({ ...prev, jump_service_point_seq: '' }));
      return;
    }

    try {
      const excludeParam = mode === 'edit' && propertyId ? `?excludePropertyId=${propertyId}` : '';
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/properties/validate-service-point-seq/${clientId}/${value}${excludeParam}`,
        { headers: { ...auth.getAuthHeaders() } }
      );
      if (response.ok) {
        const result = await response.json();
        if (!result.isValid) {
          setValidationErrors(prev => ({
            ...prev,
            jump_service_point_seq: `Already assigned to "${result.existingProperty.name}" - Reverting back`
          }));
          setTimeout(() => {
            setFormData(prev => ({
              ...prev,
              jump_service_point_seq: originalFormData?.jump_service_point_seq
            }));
            setValidationErrors(prev => ({ ...prev, jump_service_point_seq: '' }));
          }, 2000);
        } else {
          setValidationErrors(prev => ({ ...prev, jump_service_point_seq: '' }));
        }
      }
    } catch (error) {
      console.error('Error validating service point sequence:', error);
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.service_point_name || !formData.service_point_name.trim()) {
      newErrors.service_point_name = 'Service Point Name is required';
    }

    if (!formData.primary_address_line1 || !formData.primary_address_line1.trim()) {
      newErrors.primary_address_line1 = 'Primary address is required';
    }

    setErrors(newErrors);

    const hasValidationErrors = Object.values(validationErrors).some(err => err !== '');
    return Object.keys(newErrors).length === 0 && !hasValidationErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const url = mode === 'add'
        ? `${process.env.REACT_APP_API_URL}/api/admin/clients/${clientId}/properties`
        : `${process.env.REACT_APP_API_URL}/api/admin/properties/${propertyId}`;

      const response = await fetch(url, {
        method: mode === 'add' ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', ...auth.getAuthHeaders() },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setOriginalFormData({ ...formData });
        setSuccessMessage('saved');
        setTimeout(() => {
          setSuccessMessage('');
          onSuccess?.();
        }, 3000);
      } else {
        throw new Error('Failed to save property');
      }
    } catch (error) {
      console.error('Error saving property:', error);
      setErrors({ submit: 'Failed to save property. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const CardIcon = ({ type }: { type: string }) => {
    const iconProps = { style: styles.cardHeaderIcon, fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' };

    switch (type) {
      case 'service-point':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      case 'property':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'occupancy':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'address':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        );
      default:
        return null;
    }
  };

  const Toggle = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
    <div
      style={{ ...styles.toggle, ...(checked ? styles.toggleOn : styles.toggleOff) }}
      onClick={onChange}
    >
      <div style={{ ...styles.toggleSlider, ...(checked ? styles.toggleSliderOn : styles.toggleSliderOff) }} />
    </div>
  );

  return (
    <div style={styles.backdrop}>
      <GlobalStyles />
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <h2 style={styles.title}>{mode === 'add' ? 'Add New Property' : 'Edit Property'}</h2>
            {mode === 'edit' && (
              <div style={styles.statusToggle}>
                <span>Status:</span>
                <Toggle checked={formData.active || false} onChange={() => setFormData(prev => ({ ...prev, active: !prev.active }))} />
                <span>{formData.active ? 'Active' : 'Inactive'}</span>
              </div>
            )}
          </div>
          {successMessage && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.25rem 0.75rem',
              backgroundColor: '#dcfce7',
              color: '#16a34a',
              borderRadius: '1rem',
              fontSize: '0.75rem',
              fontWeight: 500,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              Record Saved Successfully
            </div>
          )}
          <button style={styles.closeBtn} onClick={onClose} disabled={loading}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.body}>
            {errors.submit && <div style={styles.errorMessage}>{errors.submit}</div>}

            {/* Top Row - 3 Cards */}
            <div style={styles.cardsRow}>
              {/* Service Point Card */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <CardIcon type="service-point" />
                  <h3 style={styles.cardTitle}>Service Point Details</h3>
                </div>
                <div style={styles.cardBody}>
                  <div style={{ ...styles.fieldGrid, ...styles.fieldGridTwoCol }}>
                    <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                      <label style={styles.label}>Service Point Name <span style={styles.required}>*</span></label>
                      <input
                        type="text"
                        name="service_point_name"
                        value={formData.service_point_name || ''}
                        onChange={handleInputChange}
                        style={{ ...styles.input, ...(errors.service_point_name ? styles.inputError : {}) }}
                        disabled={loading}
                      />
                      {errors.service_point_name && <span style={styles.errorText}>{errors.service_point_name}</span>}
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Service Point ID</label>
                      <input
                        type="text"
                        name="jump_service_point_id"
                        value={formData.jump_service_point_id || ''}
                        onChange={handleInputChange}
                        onBlur={(e) => validateServicePointId(e.target.value)}
                        style={{ ...styles.input, ...(validationErrors.jump_service_point_id ? styles.inputError : {}) }}
                        disabled={loading}
                      />
                      {validationErrors.jump_service_point_id && <span style={styles.errorText}>{validationErrors.jump_service_point_id}</span>}
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Sequence</label>
                      <input
                        type="text"
                        name="jump_service_point_seq"
                        className="property-modal-input"
                        value={formData.jump_service_point_seq || ''}
                        onChange={handleInputChange}
                        onBlur={(e) => validateServicePointSeq(e.target.value)}
                        style={{ ...styles.input, ...(validationErrors.jump_service_point_seq ? styles.inputError : {}) }}
                        disabled={loading}
                      />
                      {validationErrors.jump_service_point_seq && <span style={styles.errorText}>{validationErrors.jump_service_point_seq}</span>}
                    </div>
                    <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                      <label style={styles.label}>Legal Entity Name</label>
                      <input
                        type="text"
                        name="len_legal_entity_name"
                        value={formData.len_legal_entity_name || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Property Details Card */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <CardIcon type="property" />
                  <h3 style={styles.cardTitle}>Property Details</h3>
                </div>
                <div style={styles.cardBody}>
                  <div style={{ ...styles.fieldGrid, ...styles.fieldGridTwoCol }}>
                    <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                      <label style={styles.label}>Property Name</label>
                      <input
                        type="text"
                        name="property_name"
                        value={formData.property_name || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        disabled={loading}
                        placeholder="Enter property name"
                      />
                    </div>
                    <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                      <label style={styles.label}>Property Type</label>
                      <CustomDropdown
                        options={propertyTypes}
                        value={formData.energy_star_property_type || ''}
                        onChange={(val) => setFormData(prev => ({ ...prev, energy_star_property_type: val }))}
                        placeholder="Select Type"
                        disabled={loading}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Year Built</label>
                      <input
                        type="text"
                        name="year_built"
                        value={formData.year_built || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        disabled={loading}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}># Buildings</label>
                      <input
                        type="text"
                        name="number_of_buildings"
                        value={formData.number_of_buildings || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        disabled={loading}
                      />
                    </div>
                    <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                      <label style={styles.label}>Total Floor Area (sq ft)</label>
                      <input
                        type="text"
                        name="total_gross_floor_area"
                        value={formData.total_gross_floor_area || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Occupancy & Tax Card */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <CardIcon type="occupancy" />
                  <h3 style={styles.cardTitle}>Occupancy & Tax</h3>
                </div>
                <div style={styles.cardBody}>
                  <div style={{ ...styles.fieldGrid, ...styles.fieldGridTwoCol }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Occupancy Type</label>
                      <CustomDropdown
                        options={occupancyTypes}
                        value={formData.occupancy_type || ''}
                        onChange={(val) => setFormData(prev => ({ ...prev, occupancy_type: val }))}
                        placeholder="Select Type"
                        disabled={loading}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Occupancy %</label>
                      <input
                        type="text"
                        name="occupancy_percent"
                        value={formData.occupancy_percent || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        disabled={loading}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Tax Exempt %</label>
                      <input
                        type="text"
                        name="tax_exempt_percent"
                        value={formData.tax_exempt_percent || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        disabled={loading}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Irrigated Area</label>
                      <div style={{ paddingTop: '0.5rem' }}>
                        <Toggle
                          checked={formData.has_irrigated_area || false}
                          onChange={() => setFormData(prev => ({ ...prev, has_irrigated_area: !prev.has_irrigated_area }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Row - 3 Address Cards */}
            <div style={styles.cardsRow}>
              {/* Primary Address Card */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <CardIcon type="address" />
                  <h3 style={styles.cardTitle}>Primary Address</h3>
                </div>
                <div style={styles.cardBody}>
                  <div style={styles.fieldGrid}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Street Address <span style={styles.required}>*</span></label>
                      <input
                        type="text"
                        name="primary_address_line1"
                        value={formData.primary_address_line1 || ''}
                        onChange={handleInputChange}
                        style={{ ...styles.input, ...(errors.primary_address_line1 ? styles.inputError : {}) }}
                        disabled={loading}
                      />
                      {errors.primary_address_line1 && <span style={styles.errorText}>{errors.primary_address_line1}</span>}
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Address Line 2</label>
                      <input
                        type="text"
                        name="primary_address_line2"
                        value={formData.primary_address_line2 || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        disabled={loading}
                        placeholder="Apt, Suite, Unit, etc."
                      />
                    </div>
                    <div style={styles.addressRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>ZIP Code</label>
                        <input
                          type="text"
                          name="primary_zip_code"
                          value={formData.primary_zip_code || ''}
                          onChange={(e) => handleZipCodeChange(e, 'primary')}
                          style={styles.input}
                          disabled={loading}
                          maxLength={5}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>City</label>
                        <input
                          type="text"
                          name="primary_city"
                          value={formData.primary_city || ''}
                          onChange={handleInputChange}
                          style={{ ...styles.input, ...styles.inputDisabled }}
                          disabled
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>State</label>
                        <input
                          type="text"
                          name="primary_state"
                          value={formData.primary_state || ''}
                          onChange={handleInputChange}
                          style={{ ...styles.input, ...styles.inputDisabled }}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Address Card */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <CardIcon type="address" />
                  <h3 style={styles.cardTitle}>Service Address</h3>
                </div>
                <div style={styles.cardBody}>
                  <div style={styles.fieldGrid}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Street Address</label>
                      <input
                        type="text"
                        name="service_address_line1"
                        value={formData.service_address_line1 || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        disabled={loading}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Address Line 2</label>
                      <input
                        type="text"
                        name="service_address_line2"
                        value={formData.service_address_line2 || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        disabled={loading}
                        placeholder="Apt, Suite, Unit, etc."
                      />
                    </div>
                    <div style={styles.addressRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>ZIP Code</label>
                        <input
                          type="text"
                          name="service_zip_code"
                          value={formData.service_zip_code || ''}
                          onChange={(e) => handleZipCodeChange(e, 'service')}
                          style={styles.input}
                          disabled={loading}
                          maxLength={5}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>City</label>
                        <input
                          type="text"
                          name="service_city"
                          value={formData.service_city || ''}
                          onChange={handleInputChange}
                          style={{ ...styles.input, ...styles.inputDisabled }}
                          disabled
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>State</label>
                        <input
                          type="text"
                          name="service_state"
                          value={formData.service_state || ''}
                          onChange={handleInputChange}
                          style={{ ...styles.input, ...styles.inputDisabled }}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Billing Address Card */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <CardIcon type="address" />
                  <h3 style={styles.cardTitle}>Billing Address</h3>
                </div>
                <div style={styles.cardBody}>
                  <div style={styles.fieldGrid}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Street Address</label>
                      <input
                        type="text"
                        name="billing_address_line1"
                        value={formData.billing_address_line1 || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        disabled={loading}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Address Line 2</label>
                      <input
                        type="text"
                        name="billing_address_line2"
                        value={formData.billing_address_line2 || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        disabled={loading}
                        placeholder="Apt, Suite, Unit, etc."
                      />
                    </div>
                    <div style={styles.addressRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>ZIP Code</label>
                        <input
                          type="text"
                          name="billing_zip_code"
                          value={formData.billing_zip_code || ''}
                          onChange={(e) => handleZipCodeChange(e, 'billing')}
                          style={styles.input}
                          disabled={loading}
                          maxLength={5}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>City</label>
                        <input
                          type="text"
                          name="billing_city"
                          value={formData.billing_city || ''}
                          onChange={handleInputChange}
                          style={{ ...styles.input, ...styles.inputDisabled }}
                          disabled
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>State</label>
                        <input
                          type="text"
                          name="billing_state"
                          value={formData.billing_state || ''}
                          onChange={handleInputChange}
                          style={{ ...styles.input, ...styles.inputDisabled }}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.footer}>
            <button type="button" style={styles.btnCancel} onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.btnSave,
                ...((loading || !isDirty || Object.values(validationErrors).some(err => err !== '')) ? styles.btnSaveDisabled : {})
              }}
              disabled={loading || !isDirty || Object.values(validationErrors).some(err => err !== '')}
            >
              {loading ? 'Saving...' : (mode === 'add' ? 'Create Property' : 'Update Property')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertyModal;
